import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { ExceptionDashboard } from "./ExceptionDashboard";
import { Avatar } from "./primitives";
import { AVATARS } from "@/data/exceptionHub";
import { lineParent, lineChild, EASE } from "@/lib/motion";

const HEADLINE = ["Detect Issues Early.", "Resolve Them Faster."];

const MaskedLine = ({ children }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span variants={lineChild} className="block">{children}</motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const dashY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const dashRotate = useTransform(scrollYProgress, [0, 1], [0, -1.5]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} id="top" className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 -z-10 grid-bg" />
      <div className="absolute inset-x-0 top-0 -z-10 h-[70%] bg-gradient-to-b from-white via-white to-canvas-soft" />
      <motion.div style={{ y: glowY }} className="absolute -z-10 top-24 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-brand/10 blur-[120px]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="flex justify-center">
          <span data-testid="hero-eyebrow" className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 shadow-soft text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">
            <Sparkles className="h-3.5 w-3.5 text-brand" strokeWidth={2} />
            Workspace Product
          </span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.08 }}
          className="mt-6 text-center text-brand font-heading font-medium text-lg md:text-xl tracking-tight" data-testid="hero-product-name">
          Exception Hub™
        </motion.h1>

        <motion.div variants={lineParent} initial="hidden" animate="show"
          className="mt-3 text-center font-heading font-bold text-navy tracking-tighter leading-[1.02] text-5xl md:text-6xl lg:text-7xl">
          {HEADLINE.map((line, i) => (<MaskedLine key={i}>{line}</MaskedLine>))}
        </motion.div>

        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.55 }}
          className="mt-7 max-w-2xl mx-auto text-center text-ink-500 text-lg leading-relaxed text-balance" data-testid="hero-description">
          Monitor every shipment, document, supplier and milestone from one intelligent Workspace.
          Exception Hub™ automatically highlights operational exceptions so your team can act before delays become costly.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.68 }}
          className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href="#dashboard" data-testid="hero-primary-cta" className="group inline-flex items-center gap-2 h-12 px-7 rounded-full bg-navy text-white font-medium hover:bg-navy-soft transition-colors shadow-float">
            Explore Exception Hub™
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </a>
          <a href="#final-cta" data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-navy font-medium border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors">
            Create Your Free Workspace
          </a>
        </motion.div>

        <motion.div style={{ y: dashY, rotate: dashRotate }} initial={{ opacity: 0, y: 60, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.5 }} className="relative mt-16 md:mt-20 max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3, duration: 0.7, ease: EASE }}
            className="hidden lg:flex absolute -left-10 top-24 z-20 items-center gap-2.5 rounded-2xl bg-white border border-slate-100 shadow-card px-4 py-3 animate-float">
            <Avatar src={AVATARS.female} alt="Owner" size="h-8 w-8" />
            <div className="leading-tight">
              <p className="text-xs font-semibold text-navy">Assigned to Elena</p>
              <p className="text-[11px] text-done-text">Resolving now</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.5, duration: 0.7, ease: EASE }}
            className="hidden lg:flex absolute -right-8 bottom-28 z-20 items-center gap-2.5 rounded-2xl bg-white border border-slate-100 shadow-card px-4 py-3 animate-float" style={{ animationDelay: "1.2s" }}>
            <span className="grid place-items-center h-8 w-8 rounded-xl bg-priority-bg"><span className="h-2.5 w-2.5 rounded-full bg-priority-text" /></span>
            <div className="leading-tight">
              <p className="text-xs font-semibold text-navy">3 High Priority</p>
              <p className="text-[11px] text-ink-400">Surfaced automatically</p>
            </div>
          </motion.div>

          <ExceptionDashboard />
        </motion.div>
      </div>
    </section>
  );
};
